import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  useColorModeValue,
  Heading,
  Card,
  CardBody,
  useToast,
  Select,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { userAPI, User } from '../services/api';

const statusOptions: User['status'][] = ['active', 'inactive', 'onboarding'];

interface FormErrors {
  name?: string;
  email?: string;
  role?: string;
}

const EditUser: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState<Partial<User>>({
    name: '',
    email: '',
    role: '',
    status: 'active',
    joinDate: '',
  });

  // Refs for focus management
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const roleRef = useRef<HTMLInputElement>(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    const fetchUser = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const response = await userAPI.getUserById(id);
        const userData = response.data;
        setUser(userData);
        setFormData({
          name: userData.name || '',
          email: userData.email || '',
          role: userData.role || '',
          status: userData.status || 'active',
          joinDate: userData.joinDate ? new Date(userData.joinDate).toISOString().split('T')[0] : '',
        });
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error.response?.data?.message || 'Failed to fetch user data',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate('/users');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id, navigate, toast]);

  const validateField = (field: keyof FormErrors, value: string): string | undefined => {
    switch (field) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        return undefined;
      case 'email':
        if (!value.trim()) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Please enter a valid email address';
        return undefined;
      case 'role':
        if (!value.trim()) return 'Role is required';
        if (value.trim().length < 2) return 'Role must be at least 2 characters';
        return undefined;
      default:
        return undefined;
    }
  };

  const handleInputChange = (field: keyof User, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user types
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleBlur = (field: keyof FormErrors) => {
    const value = formData[field as keyof User];
    const error = validateField(field, value as string);
    setErrors(prev => ({
      ...prev,
      [field]: error,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    newErrors.name = validateField('name', formData.name || '');
    newErrors.email = validateField('email', formData.email || '');
    newErrors.role = validateField('role', formData.role || '');

    setErrors(newErrors);

    // Focus on first invalid field
    if (newErrors.name) {
      nameRef.current?.focus();
      return false;
    }
    if (newErrors.email) {
      emailRef.current?.focus();
      return false;
    }
    if (newErrors.role) {
      roleRef.current?.focus();
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    if (!id) {
      toast({
        title: 'Error',
        description: 'User ID not found',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setSaving(true);
      await userAPI.updateUser(id, formData);
      
      toast({
        title: 'User Updated',
        description: 'User details have been updated successfully',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      
      navigate('/users');
    } catch (error: any) {
      toast({
        title: 'Update Failed',
        description: error.response?.data?.message || 'Failed to update user',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleBackToUsers = () => {
    navigate('/users');
  };

  if (loading) {
    return (
      <Box>
        <Text>Loading user data...</Text>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box>
        <Text>User not found</Text>
        <Button onClick={handleBackToUsers} mt={4}>
          Back to Users
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header with Back Navigation */}
      <Box mb={8}>
        <HStack spacing={4} mb={4}>
          <Button
            leftIcon={<ArrowBackIcon />}
            variant="ghost"
            onClick={handleBackToUsers}
            colorScheme="primary"
          >
            Back to Users
          </Button>
        </HStack>
        <Heading size="lg" color="gray.800" mb={2}>
          Edit User
        </Heading>
        <Text color="gray.600">
          Update user information and settings
        </Text>
      </Box>

      {/* Edit Form */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardBody>
          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <VStack spacing={6} align="stretch">
            <HStack spacing={4} flexWrap="wrap">
              <FormControl flex={1} minW="300px" isInvalid={!!errors.name}>
                <FormLabel>Name <span className="text-red-500">*</span></FormLabel>
                <Input
                  id="name"
                  ref={nameRef}
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  onBlur={() => handleBlur('name')}
                  placeholder="Enter user name"
                  borderColor={errors.name ? 'red.500' : undefined}
                  _focus={{ borderColor: errors.name ? 'red.500' : undefined }}
                />
                <FormErrorMessage>{errors.name}</FormErrorMessage>
              </FormControl>
              
              <FormControl flex={1} minW="300px" isInvalid={!!errors.email}>
                <FormLabel>Email <span className="text-red-500">*</span></FormLabel>
                <Input
                  id="email"
                  ref={emailRef}
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                  placeholder="Enter email address"
                  borderColor={errors.email ? 'red.500' : undefined}
                  _focus={{ borderColor: errors.email ? 'red.500' : undefined }}
                />
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>
            </HStack>

            <HStack spacing={4} flexWrap="wrap">
              <FormControl flex={1} minW="300px" isInvalid={!!errors.role}>
                <FormLabel>Role <span className="text-red-500">*</span></FormLabel>
                <Input
                  id="role"
                  ref={roleRef}
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  onBlur={() => handleBlur('role')}
                  placeholder="Enter user role"
                  borderColor={errors.role ? 'red.500' : undefined}
                  _focus={{ borderColor: errors.role ? 'red.500' : undefined }}
                />
                <FormErrorMessage>{errors.role}</FormErrorMessage>
              </FormControl>
              
              <FormControl flex={1} minW="300px">
                <FormLabel>Status</FormLabel>
                <Select
                  id="status"
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value as User['status'])}
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </HStack>

            <FormControl maxW="300px">
              <FormLabel>Join Date</FormLabel>
              <Input
                id="joinDate"
                type="date"
                value={formData.joinDate}
                onChange={(e) => handleInputChange('joinDate', e.target.value)}
              />
            </FormControl>

            {/* Action Buttons */}
            <HStack spacing={4} pt={4}>
              <Button
                colorScheme="primary"
                type="submit"
                isLoading={saving}
                loadingText="Saving..."
                minW="120px"
              >
                Save Changes
              </Button>
              <Button
                variant="outline"
                onClick={handleBackToUsers}
                type="button"
                minW="120px"
              >
                Cancel
              </Button>
            </HStack>
          </VStack>
          </form>
        </CardBody>
      </Card>
    </Box>
  );
};

export default EditUser;
